import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Edit3, Trash2, ToggleLeft, ToggleRight, Store, Mail, KeyRound } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';

export default function AdminStoreAdmins() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, else user object
  const [form, setForm] = useState({ name: '', email: '', password: '', supermarket_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/store-admins`, { withCredentials: true }),
        axios.get(`${API}/admin/supermarkets`, { withCredentials: true }),
      ]);
      setAdmins(a.data);
      setSupermarkets(s.data);
    } catch (err) {
      console.error('Failed to load store admins:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', supermarket_id: supermarkets[0]?.id || '' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', supermarket_id: u.assigned_supermarket_id });
    setError('');
    setShowForm(true);
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        const payload = { name: form.name, supermarket_id: form.supermarket_id };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admin/store-admins/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/store-admins`, form, { withCredentials: true });
      }
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/admin/store-admins/${u.id}`, { active: !u.active }, { withCredentials: true });
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not update');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/store-admins/${u.id}`, { withCredentials: true });
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <div className="admin-page" data-testid="admin-store-admins-page">
      <button className="back-btn" onClick={() => navigate('/admin')} data-testid="back-btn">
        <ArrowLeft /> Admin Dashboard
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Store Admin Accounts</h2>
        <button onClick={openCreate} data-testid="create-store-admin-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none', background: 'var(--lime)', color: 'var(--navy)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          <UserPlus size={16} /> Invite Store Admin
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.86rem', marginTop: 6 }}>
        Create an admin account for each supermarket. Store admins can only manage their assigned supermarket's products, orders, and branches.
      </p>

      {loading ? (
        <div className="loading-screen">Loading…</div>
      ) : admins.length === 0 ? (
        <div className="empty-state" data-testid="no-store-admins">
          <p>No store admin accounts yet. Invite your first supermarket admin to start delegating catalog management.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          {admins.map(u => (
            <div key={u.id} data-testid={`store-admin-row-${u.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--white)', border: '1px solid var(--border-gray)', borderRadius: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {u.name}
                  {!u.active && <span style={{ background: '#FEE2E2', color: '#991B1B', fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>DISABLED</span>}
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <Mail size={12} /> {u.email}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#6B21A8', display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, fontWeight: 600 }}>
                  <Store size={12} /> {u.assigned_supermarket_name || '(no store)'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}
                  title={u.active ? 'Disable login' : 'Enable login'}
                  style={{ padding: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: u.active ? 'var(--success)' : 'var(--text-muted)' }}>
                  {u.active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
                <button onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}
                  style={{ padding: '8px 12px', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--navy)', background: 'var(--white)', color: 'var(--navy)', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Edit3 size={13} /> Edit
                </button>
                <button onClick={() => remove(u)} data-testid={`delete-${u.id}`}
                  style={{ padding: '8px 12px', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #DC2626', background: 'var(--white)', color: '#DC2626', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="store-admin-form">
            <h3>{editing ? `Edit ${editing.name}` : 'Invite Store Admin'}</h3>
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="sa-name-input" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} disabled={!!editing}
                onChange={e => setForm({ ...form, email: e.target.value })} data-testid="sa-email-input"
                placeholder="store-admin@example.com" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <KeyRound size={13} /> Password {editing && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.78rem' }}>(leave blank to keep current)</span>}
              </label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                data-testid="sa-password-input" placeholder={editing ? 'Leave blank to keep' : 'Minimum 8 characters'} />
            </div>
            <div className="form-group">
              <label>Assigned Supermarket</label>
              <select value={form.supermarket_id} onChange={e => setForm({ ...form, supermarket_id: e.target.value })} data-testid="sa-supermarket-select">
                <option value="">— Choose a supermarket —</option>
                {supermarkets.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            {error && (
              <div data-testid="sa-form-error" style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: '0.85rem', marginTop: 8 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn-lime" onClick={submit} disabled={saving} data-testid="sa-submit-btn" style={{ flex: 1 }}>
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Admin')}
              </button>
              <button onClick={() => setShowForm(false)} data-testid="sa-cancel-btn"
                style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border-gray)', background: 'white', color: 'var(--text-dark)', fontFamily: 'inherit', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
